import { useState } from "react"

import { Button, FormActions, FormField, FormFieldset, Input } from "@coval/ui"

import { CreateResourceButton } from "@/components/ResourceActionButton"

const NAME_MAX_LENGTH = 200

export interface WorkspaceCreateDraft {
  displayName: string
}

interface WorkspaceCreateFormProps {
  error: string
  isPending: boolean
  onCancel: () => void
  onSubmit: (draft: WorkspaceCreateDraft) => void
  /** Clears the submit error as the viewer edits the name. */
  onDirty: () => void
}

/** Validates a new workspace's name before handing it to the mutation owner. */
export function WorkspaceCreateForm({
  error,
  isPending,
  onCancel,
  onDirty,
  onSubmit,
}: WorkspaceCreateFormProps) {
  const [name, setName] = useState("")
  const [nameError, setNameError] = useState("")
  const trimmedName = name.trim()
  const nameValidationError =
    !trimmedName || trimmedName.length > NAME_MAX_LENGTH
      ? `Workspace name must be between 1 and ${NAME_MAX_LENGTH} characters.`
      : null

  const showValidationError = () => {
    setNameError(nameValidationError ?? "")
  }

  const handleSubmit = () => {
    showValidationError()

    if (nameValidationError) {
      return
    }

    onSubmit({ displayName: trimmedName })
  }

  return (
    <form
      data-testid="workspace-create-form"
      onSubmit={(event) => {
        event.preventDefault()
        handleSubmit()
      }}
    >
      <FormFieldset>
        <FormField label="Workspace name" error={nameError}>
          {(ids) => (
            <Input
              {...ids}
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                setNameError("")
                onDirty()
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && nameValidationError) {
                  event.preventDefault()
                  showValidationError()
                }
              }}
              disabled={isPending}
              error={Boolean(nameError)}
              data-testid="workspace-create-name"
            />
          )}
        </FormField>

        {error && (
          <div role="alert" className="text-xs text-coval-danger-text">
            {error}
          </div>
        )}
      </FormFieldset>

      <FormActions>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isPending}
        >
          Cancel
        </Button>
        <CreateResourceButton
          resourceName="Workspace"
          validationIssues={[nameValidationError]}
          onValidationAttempt={showValidationError}
          type="submit"
          disabled={isPending}
          loading={isPending}
          data-testid="workspace-create-confirm"
        />
      </FormActions>
    </form>
  )
}
